import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { WHATSAPP_CATALOG_URL } from "@/lib/config";
import { fragranceAudiences } from "@/lib/fragranceAudience";

const Shop = () => {
  const { t, tl, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const activeSub = params.get("sub") || "all";
  const activeFamily = params.get("family") || "all";
  const activeView = params.get("view") || "all";
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/olfactory-families").then((r) => setFamilies(r.data));
  }, []);

  useEffect(() => {
    let q = [];
    if (activeCat !== "all") q.push(`category=${activeCat}`);
    if (activeSub !== "all") q.push(`subcategory=${activeSub}`);
    if (activeFamily !== "all") q.push(`family=${activeFamily}`);
    if (activeView === "offers") q.push("offers=true");
    api.get(`/products${q.length ? "?" + q.join("&") : ""}`).then((r) => setProducts(r.data));
  }, [activeCat, activeSub, activeFamily, activeView]);

  useEffect(() => setPage(1), [activeCat, activeSub, activeFamily, activeView]);

  const searchedProducts = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(lang === "it" ? "it-IT" : "en-US");
    if (!query) return products;
    return products.filter((product) => [
      product.name?.it, product.name?.en, product.slug,
      product.description?.it, product.description?.en,
      product.family?.it, product.family?.en,
    ].filter(Boolean).join(" ").toLocaleLowerCase(lang === "it" ? "it-IT" : "en-US").includes(query));
  }, [products, search, lang]);
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(searchedProducts.length / pageSize));
  const visibleProducts = useMemo(() => {
    if (pageSize === "all") return searchedProducts;
    const start = (page - 1) * pageSize;
    return searchedProducts.slice(start, start + pageSize);
  }, [searchedProducts, page, pageSize]);
  useEffect(() => setPage((current) => Math.min(current, totalPages)), [totalPages]);
  useEffect(() => setPage(1), [search]);

  const setView = (view) => {
    const next = new URLSearchParams(params);
    if (view === "all") next.delete("view");
    else next.set("view", view);
    setParams(next);
  };

  const setCat = (c) => setParams(c === "all" ? {} : { cat: c });
  const setSub = (s) => {
    const next = new URLSearchParams(params);
    if (s === "all") next.delete("sub"); else next.set("sub", s);
    setParams(next);
  };
  const setFamily = (family) => {
    const next = { cat: "profumi" };
    if (activeSub !== "all") next.sub = activeSub;
    if (family !== "all") next.family = family;
    setParams(next);
  };

  const tabs = [{ slug: "all", name: { it: t("shop.all"), en: t("shop.all") } }, ...categories];
  const currentCat = categories.find((c) => c.slug === activeCat);
  const subTabs = activeCat === "profumi" ? fragranceAudiences : currentCat?.subcategories || [];

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
      <Reveal>
        <div className="mb-2 md:mb-4">
          <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-champagne mb-2 md:mb-4">Kaós®</p>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-charcoal">{t("shop.title")}</h1>
          <p className="text-sm md:text-base text-stone-500 mt-2 md:mt-4 max-w-md">{t("shop.subtitle")}</p>
        </div>
      </Reveal>

      <a
        href={WHATSAPP_CATALOG_URL}
        target="_blank"
        rel="noreferrer"
        className="mt-4 md:mt-5 inline-flex items-center border-b border-stone-300 pb-1 text-[10px] md:text-xs uppercase tracking-[0.12em] md:tracking-[0.18em] text-stone-500 hover:text-champagne hover:border-champagne transition-colors"
      >
        {t("shop.whatsappCatalog")}
      </a>

      <div className="-mx-4 mt-5 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:mt-5 md:flex-wrap md:gap-3 md:px-0" aria-label="Filtri promozioni e disponibilità">
        {[["all", t("shop.allProducts")], ["offers", t("shop.offers")]].map(([value, label]) => (
          <button key={value} type="button" onClick={() => setView(value)}
            className={`shrink-0 whitespace-nowrap px-3 py-2 text-[10px] md:text-xs uppercase tracking-[0.1em] md:tracking-[0.14em] border transition-colors ${activeView === value ? "border-charcoal bg-charcoal text-white" : "border-stone-200 bg-white text-stone-500 hover:border-charcoal"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="-mx-4 flex gap-5 overflow-x-auto border-b border-stone-200 mt-6 mb-5 px-4 md:mx-0 md:flex-wrap md:gap-6 md:mt-6 md:mb-4 md:px-0">
        {tabs.map((c) => (
          <button
            key={c.slug}
            onClick={() => setCat(c.slug)}
            data-testid={`shop-filter-${c.slug}`}
            className={`shrink-0 whitespace-nowrap pb-3 md:pb-4 text-[10px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em] transition-colors relative ${
              activeCat === c.slug ? "text-charcoal" : "text-stone-400 hover:text-stone-600"
            }`}
          >
            {tl(c.name)}
            {activeCat === c.slug && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-charcoal" />}
          </button>
        ))}
      </div>

      {subTabs.length > 0 && (
        <div role="group" aria-label={activeCat === "profumi" ? "Filtra profumi per destinatario" : "Sottocategorie"} className="-mx-4 flex flex-wrap gap-2 px-4 pb-1 mb-7 md:mx-0 md:gap-3 md:px-0 md:mb-6">
          {[{ slug: "all", name: { it: "Tutte", en: "All" } }, ...subTabs].map((s) => (
            <button
              key={s.slug}
              onClick={() => setSub(s.slug)}
              data-testid={`shop-sub-${s.slug}`}
              aria-pressed={activeSub === s.slug}
              className={`shrink-0 whitespace-nowrap px-3 py-2 text-[10px] md:text-xs uppercase tracking-[0.1em] md:tracking-[0.14em] border transition-colors ${
                activeSub === s.slug ? "border-charcoal bg-charcoal text-white" : "border-stone-300 text-stone-500 hover:border-charcoal"
              }`}
            >
              {tl(s.name)}
            </button>
          ))}
        </div>
      )}
      {activeCat === "profumi" && families.length > 0 && (
        <div className="mb-7 md:mb-6 border-t border-stone-200 pt-4 md:pt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-3">
            {t("shop.olfactoryFamilies")}
          </p>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:gap-3 md:px-0">
            {[{ slug: "all", name: { it: "Tutte", en: "All" } }, ...families].map((family) => (
              <button
                key={family.slug}
                onClick={() => setFamily(family.slug)}
                data-testid={`shop-family-${family.slug}`}
                className={`shrink-0 whitespace-nowrap px-3 py-2 text-[10px] md:text-xs border transition-colors ${
                  activeFamily === family.slug
                    ? "border-champagne bg-champagne-soft text-charcoal"
                    : "border-stone-300 text-stone-500 hover:border-charcoal"
                }`}
              >
                {tl(family.name)}
              </button>
            ))}
          </div>
        </div>
      )}
      {subTabs.length === 0 && activeCat !== "profumi" && <div className="mb-4" />}

      <div className="mb-4">
        <label className="relative block" htmlFor="shop-product-search">
          <span className="sr-only">{lang === "it" ? "Cerca un prodotto" : "Search for a product"}</span>
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            id="shop-product-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={lang === "it" ? "Cerca per nome, numero o famiglia olfattiva…" : "Search by name, number or fragrance family…"}
            className="h-12 w-full border border-stone-300 bg-white pl-12 pr-12 text-sm text-charcoal outline-none transition-colors placeholder:text-stone-400 focus:border-charcoal"
          />
          {search && <button type="button" onClick={() => setSearch("")} aria-label={lang === "it" ? "Cancella ricerca" : "Clear search"} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-stone-400 hover:text-charcoal"><X size={16}/></button>}
        </label>
      </div>

      {products.length > 0 && <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-y border-stone-200 py-3">
        <p className="text-xs text-stone-500">
          {lang === "it" ? `${searchedProducts.length} prodotti trovati` : `${searchedProducts.length} products found`}
        </p>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-stone-500">
          {lang === "it" ? "Mostra" : "Show"}
          <select
            value={pageSize}
            onChange={(event) => {
              const value = event.target.value;
              setPageSize(value === "all" ? "all" : Number(value));
              setPage(1);
            }}
            className="border border-stone-300 bg-white px-3 py-2 text-xs text-charcoal"
            aria-label={lang === "it" ? "Prodotti per pagina" : "Products per page"}
          >
            {[10, 20, 30, 40].map((size) => <option key={size} value={size}>{size}</option>)}
            <option value="all">{lang === "it" ? "Tutti" : "All"}</option>
          </select>
          {lang === "it" ? "per pagina" : "per page"}
        </label>
      </div>}

      {searchedProducts.length === 0 ? (
        <div className="py-20 text-center"><p className="text-stone-400">{search ? (lang === "it" ? "Nessun prodotto corrisponde alla ricerca." : "No products match your search.") : t("shop.empty")}</p>{search && <button type="button" onClick={()=>setSearch("")} className="mt-4 border-b border-charcoal pb-1 text-[10px] uppercase tracking-wider">{lang === "it" ? "Cancella ricerca" : "Clear search"}</button>}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 sm:gap-x-6 gap-y-9 md:gap-y-14">
            {visibleProducts.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 100}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
          {totalPages > 1 && <nav className="mt-12 flex flex-wrap items-center justify-center gap-2 border-t border-stone-200 pt-6" aria-label={lang === "it" ? "Paginazione prodotti" : "Product pagination"}>
            <button type="button" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="border border-stone-300 px-3 py-2 text-[10px] uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-30">
              {lang === "it" ? "Precedente" : "Previous"}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => <button key={number} type="button" onClick={() => setPage(number)} aria-current={page === number ? "page" : undefined} className={`h-9 min-w-9 border px-2 text-xs ${page === number ? "border-charcoal bg-charcoal text-white" : "border-stone-300 bg-white text-stone-600"}`}>{number}</button>)}
            <button type="button" disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))} className="border border-stone-300 px-3 py-2 text-[10px] uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-30">
              {lang === "it" ? "Successiva" : "Next"}
            </button>
          </nav>}
        </>
      )}
    </div>
  );
};

export default Shop;
